import { fetchCircuits } from '../circuits/api.js';
import { fetchCars } from '../cars/api.js';
import { fetchDrivers } from '../drivers/api.js';
import { fetchPlayedRacesCount, fetchRaceHistoriesPage } from '../raceHistory/api.js';
import { archivesDetailHash } from '../raceHistory/archivesHash.js';
import { fetchActiveGlobalSeason } from '../championship/api.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import type { RaceHistorySummary } from '@multirx/shared/types/raceHistory.js';
import { pillButtonHtml } from '../ui/pillButton.js';
import { glowCardHtml } from '../ui/glowCard.js';
import { kpiMosaicHtml, updateKpiStatCards } from '../ui/kpiStatCard.js';
import { buildSeasonTeaserModel, seasonTeaserCardHtml } from './dashboardSeasonTeaserModel.js';
import { mountFeaturedCarCard } from './mountFeaturedCarCard.js';
import { mountFeaturedPilotCard } from './mountFeaturedPilotCard.js';
import { MULTIRX_LOGO_SRC } from './multirxHero.js';
import { bindStandingsDriverTooltips } from '../standings/standingsDriverTooltip.js';
import { bindStandingsPointsBreakdownTooltips } from '../standings/standingsPointsBreakdownTooltip.js';

const KPI_ICONS = {
  circuits: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg>`,
  cars: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 11l1.5-4.5h11L19 11v5a1 1 0 0 1-1 1h-1a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H4a1 1 0 0 1-1-1v-5zm2.2-3h9.6L16.5 9H7.5l-.3-1z"/></svg>`,
  drivers: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm-8 9a8 8 0 0 1 16 0H4z"/></svg>`,
  races: `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 15.5A8.5 8.5 0 0 1 12.5 7H20v2h-2.05A6.5 6.5 0 1 0 18 17h-1.1A8 8 0 0 1 4 15.5zM19 12l-3.5 2 1 3-1.8 1 1.8 1-1 3L19 22l3.5-2-1-3 1.8-1-1.8-1 1-3L19 12z"/></svg>`,
} as const;

const KPI_CONFIG = [
  {
    id: 'circuits',
    title: 'Circuits',
    accent: 'var(--color-primary)',
    href: '#garage/circuits',
  },
  {
    id: 'cars',
    title: 'Voitures',
    accent: 'var(--color-accent)',
    href: '#garage/voitures',
  },
  {
    id: 'drivers',
    title: 'Pilotes',
    accent: 'var(--color-primary-hover, #b24a5c)',
    href: '#garage/pilotes',
  },
  {
    id: 'races',
    title: 'Parties jouées',
    accent: 'color-mix(in srgb, var(--color-primary) 72%, #fff)',
    href: '#archives',
  },
] as const;

function wireHashLinks(root: ParentNode): void {
  root.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const href = a.getAttribute('href');
      if (href) window.location.hash = href.replace(/^#/, '');
    });
  });
}

function formatRaceDate(iso?: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function divisionLabel(divisionId: number): string {
  return DIVISIONS.find((d) => d.id === divisionId)?.shortLabel ?? `D${divisionId}`;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function winnerLine(race: RaceHistorySummary): string {
  if (race.winnerDriverName) {
    return `Vainqueur · ${escapeHtml(race.winnerDriverName)}`;
  }
  return 'Résultats à consulter';
}

function recentRaceItemHtml(race: RaceHistorySummary): string {
  const date = formatRaceDate(race.createdAt);
  const meta = [date, `${race.numParticipants} pilotes`].filter(Boolean).join(' · ');
  const circuitLabel = escapeHtml(
    race.circuitName?.trim() || (race.circuitId ? `Circuit #${race.circuitId}` : 'Circuit')
  );
  return `
    <a href="#${archivesDetailHash(race.id)}" class="dashboard-race-item">
      <span class="dashboard-race-item-head">
        <strong>${circuitLabel}</strong>
        <span class="badge badge-division--${race.division}">${divisionLabel(race.division)}</span>
      </span>
      <span class="dashboard-race-item-meta">${meta}</span>
      <span class="dashboard-race-item-winner">${winnerLine(race)}</span>
    </a>`;
}

function kpiCardsHtml(): string {
  return kpiMosaicHtml(
    KPI_CONFIG.map((cfg) => ({
      id: cfg.id,
      title: cfg.title,
      accent: cfg.accent,
      href: cfg.href,
      iconSvg: KPI_ICONS[cfg.id],
    }))
  );
}

function actionTile(href: string, title: string, desc: string, featured = false): string {
  return glowCardHtml(
    { heading: title, middle: desc, accent: 'Explorer →' },
    { href, wrapClass: featured ? 'card-glow-wrap--featured' : undefined }
  );
}

export function mountDashboardApp(container: HTMLElement): void {
  container.innerHTML = '';
  container.className = 'app-view';

  container.innerHTML = `
    <div class="dashboard-page">
      <header class="dashboard-hero">
        <div class="dashboard-hero-inner">
          <div>
            <p class="dashboard-hero-eyebrow">MultiRX · Saison en cours</p>
            <h1 class="dashboard-hero-title">Tableau de bord</h1>
            <p class="dashboard-hero-sub">Vos circuits, votre garage et les saisons — tout est à portée de clic.</p>
          </div>
          <div class="dashboard-hero-actions">
            <a href="#accueil" class="dashboard-hero-logo-link" aria-label="MultiRX — Accueil">
              <img
                class="dashboard-hero-logo"
                src="${MULTIRX_LOGO_SRC}"
                alt="MultiRX"
                width="320"
                height="97"
                decoding="async"
              />
            </a>
          </div>
        </div>
      </header>

      <div class="dashboard-grid">
        <section class="dashboard-actions-section" aria-labelledby="dashboard-actions-heading">
          <div class="dashboard-section-head">
            <h2 id="dashboard-actions-heading">Raccourcis</h2>
          </div>
          <div class="dashboard-bento">
            <div id="dashboard-featured-pilot-host" class="card-glow-wrap card-glow-wrap--featured card-glow-wrap--featured-pilot"></div>
            <div id="dashboard-featured-car-host" class="card-glow-wrap card-glow-wrap--featured-car"></div>
            <div id="dashboard-season-teaser-host" class="card-glow-wrap card-glow-wrap--season-teaser">
              <div class="card-glow dashboard-season-teaser-card">
                <p class="heading">Saisons</p>
                <p class="dashboard-season-teaser__progress">Chargement…</p>
                <a href="#saisons" class="dashboard-season-teaser__cta">Voir la saison →</a>
              </div>
            </div>
            ${actionTile('#garage/circuits', 'Circuits', 'Parcourir et modifier vos tracés')}
            ${actionTile('#archives', 'Replays', 'Historique des courses jouées')}
          </div>
        </section>

        <aside class="dashboard-aside">
          <section class="dashboard-kpi-section" aria-labelledby="dashboard-kpi-heading">
            <div class="dashboard-section-head">
              <h2 id="dashboard-kpi-heading">Statistiques</h2>
            </div>
            <div class="dashboard-kpi-grid">
              ${kpiCardsHtml()}
            </div>
          </section>

          <div class="dashboard-panel">
            <h2 id="dashboard-recent-heading">Dernières courses</h2>
            <p class="dashboard-panel-lead">Les résultats récents et le classement par division.</p>
            <div id="dashboard-recent-races" class="dashboard-race-list" role="list" aria-labelledby="dashboard-recent-heading">
              <p class="dashboard-race-empty">Chargement…</p>
            </div>
            <div class="dashboard-panel-actions">
              <a href="#saisons" class="dashboard-panel-link">Voir les classements</a>
              ${pillButtonHtml('Paramètres', { href: '#parametres', variant: 'soft' })}
            </div>
          </div>
        </aside>
      </div>
    </div>
  `;

  wireHashLinks(container);

  const recentRoot = container.querySelector('#dashboard-recent-races');
  const kpiRoot = container.querySelector('.dashboard-kpi-grid');
  const featuredHost = container.querySelector('#dashboard-featured-pilot-host');
  const featuredCarHost = container.querySelector('#dashboard-featured-car-host');
  const seasonHost = container.querySelector('#dashboard-season-teaser-host');

  void renderSeasonTeaser(seasonHost);

  void Promise.all([
    fetchCircuits(),
    fetchCars(),
    fetchDrivers(),
    fetchPlayedRacesCount().catch(() => 0),
    fetchRaceHistoriesPage(6, 0)
      .then((page) => page.items)
      .catch(() => [] as RaceHistorySummary[]),
  ])
    .then(([circuits, cars, drivers, racesPlayed, recentRaces]) => {
      if (kpiRoot) {
        updateKpiStatCards(kpiRoot, {
          circuits: circuits.length,
          cars: cars.length,
          drivers: drivers.length,
          races: racesPlayed,
        });
      }

      if (featuredHost instanceof HTMLElement) {
        mountFeaturedPilotCard(featuredHost, { drivers, cars });
      }
      if (featuredCarHost instanceof HTMLElement) {
        mountFeaturedCarCard(featuredCarHost, { cars });
      }

      if (!recentRoot) return;

      if (recentRaces.length === 0) {
        recentRoot.innerHTML = `<p class="dashboard-race-empty">Aucune course terminée pour l'instant.</p>`;
        return;
      }

      recentRoot.innerHTML = recentRaces.map(recentRaceItemHtml).join('');
      wireHashLinks(recentRoot);
    })
    .catch((err) => {
      void Promise.all([
        fetchCircuits(),
        fetchCars(),
        fetchDrivers(),
        fetchPlayedRacesCount().catch(() => 0),
      ])
        .then(([circuits, cars, drivers, racesPlayed]) => {
          if (kpiRoot) {
            updateKpiStatCards(kpiRoot, {
              circuits: circuits.length,
              cars: cars.length,
              drivers: drivers.length,
              races: racesPlayed,
            });
          }
          if (featuredHost instanceof HTMLElement) {
            mountFeaturedPilotCard(featuredHost, { drivers, cars });
          }
          if (featuredCarHost instanceof HTMLElement) {
            mountFeaturedCarCard(featuredCarHost, { cars });
          }
        })
        .catch(() => {});

      if (recentRoot) {
        recentRoot.innerHTML = `<p class="dashboard-race-empty is-error">${
          err instanceof Error ? err.message : 'Impossible de charger les courses.'
        }</p>`;
      }
    });
}

async function renderSeasonTeaser(host: Element | null): Promise<void> {
  if (!(host instanceof HTMLElement)) return;
  let globalView = null;
  try {
    globalView = await fetchActiveGlobalSeason();
  } catch {
    globalView = null;
  }
  const model = buildSeasonTeaserModel(globalView);
  const tmp = document.createElement('div');
  tmp.innerHTML = seasonTeaserCardHtml(model);
  const wrap = tmp.firstElementChild;
  if (!(wrap instanceof HTMLElement)) return;
  wrap.id = 'dashboard-season-teaser-host';
  host.replaceWith(wrap);
  wireHashLinks(wrap);
  bindStandingsDriverTooltips(wrap);
  bindStandingsPointsBreakdownTooltips(wrap);
}
